import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  LinearProgress,
  CircularProgress,
  Fade,
  Zoom,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { FileItemProps } from './types';
import { formatFileSize, getFileIcon } from './utils';

export const FileItem: React.FC<FileItemProps> = ({
  file,
  onRemove,
}) => {
  const theme = useTheme();

  return (
    <Zoom in={true} timeout={300}>
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: theme.spacing(1),
          gap: 1,
          backgroundColor: file.error 
            ? theme.palette.error.light + '20'
            : file.uploadState?.isCompleted
            ? theme.palette.success.light + '10'
            : theme.palette.grey[50],
          border: file.uploadState?.isCompleted
            ? `1px solid ${theme.palette.success.main}20`
            : undefined,
          transition: 'all 0.3s ease',
        }}
      >
        {/* ファイルアイコン/プレビュー */}
        <Box sx={{ minWidth: 40, display: 'flex', justifyContent: 'center', position: 'relative' }}>
          {file.uploadState?.isUploading ? (
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress
                variant="determinate"
                value={file.uploadState.progress}
                size={32}
                sx={{ color: theme.palette.primary.main }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {getFileIcon(file.file.type, true)}
              </Box>
            </Box>
          ) : file.uploadState?.isCompleted ? (
            <Fade in={true} timeout={500}>
              <Box sx={{ position: 'relative' }}>
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt="プレビュー"
                    style={{
                      width: 32,
                      height: 32,
                      objectFit: 'cover',
                      borderRadius: 4,
                    }}
                  />
                ) : (
                  getFileIcon(file.file.type)
                )}
                <CheckIcon
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    width: 16,
                    height: 16,
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                    borderRadius: '50%',
                    fontSize: 12,
                  }}
                />
              </Box>
            </Fade>
          ) : (
            file.preview ? (
              <img
                src={file.preview}
                alt="プレビュー"
                style={{
                  width: 32,
                  height: 32,
                  objectFit: 'cover',
                  borderRadius: 4,
                }}
              />
            ) : (
              getFileIcon(file.file.type)
            )
          )}
        </Box>

        {/* ファイル情報 */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.file.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatFileSize(file.file.size)}
            {file.uploadState?.isUploading && (
              <span style={{ marginLeft: 8, color: theme.palette.primary.main }}>
                アップロード中... {Math.round(file.uploadState.progress)}%
              </span>
            )}
            {file.uploadState?.isCompleted && (
              <span style={{ marginLeft: 8, color: theme.palette.success.main }}>
                アップロード完了
              </span>
            )}
          </Typography>
          {file.error && (
            <Typography variant="caption" color="error" display="block">
              {file.error}
            </Typography>
          )}
          
          {/* プログレスバー */}
          {file.uploadState?.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadState.progress}
              sx={{
                mt: 0.5,
                height: 2,
                borderRadius: 1,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          )}
        </Box>

        {/* 削除ボタン */}
        <Tooltip title="削除">
          <IconButton
            size="small"
            onClick={() => onRemove(file.id)}
            sx={{ 
              color: theme.palette.grey[500],
              opacity: file.uploadState?.isUploading ? 0.5 : 1,
            }}
            disabled={file.uploadState?.isUploading}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </Zoom>
  );
}; 